/**
 * Color resolution utilities for widget color properties
 * Resolves ColorValue objects to hex strings using theme colors
 */

import { useCallback } from "react";
import { ColorValue } from "../types/color";
import { useThemeColors } from "./useWidgetTheme";

/**
 * Resolve a ColorValue to a hex string
 *
 * @param colorValue - The ColorValue object or legacy string value
 * @param themeColors - Record of theme color names to hex values
 * @param defaultHex - Fallback hex value if theme color is not found (default: '#000000')
 * @returns The resolved hex color string
 */
export const resolveColor = (
  colorValue: ColorValue | string | undefined | null,
  themeColors: Record<string, string>,
  defaultHex: string = "#000000",
): string => {
  // Handle null/undefined
  if (!colorValue) return defaultHex;

  // Handle legacy string values (backward compat - can be removed later)
  if (typeof colorValue === "string") return colorValue;

  // Handle ColorValue objects
  if (colorValue.type === "custom") {
    return colorValue.value;
  }

  // type === 'theme' - look up in theme colors
  const resolved = themeColors[colorValue.value];
  return resolved ?? defaultHex;
};

/** Type for the resolver function returned by useResolveColor */
export type ResolveColorFn = (
  colorValue: ColorValue | string | undefined | null,
  defaultHex?: string,
) => string;

/**
 * Creates a bound resolveColor function given themeColors
 * Used internally by useWidgetEditable and useResolveColor
 */
export const createResolveColor = (
  themeColors: Record<string, string>,
): ResolveColorFn => {
  return (
    colorValue: ColorValue | string | undefined | null,
    defaultHex: string = "#000000",
  ): string => {
    return resolveColor(colorValue, themeColors, defaultHex);
  };
};

/**
 * Hook that returns a resolveColor function with theme colors already bound
 *
 * Usage:
 * ```tsx
 * const resolveColor = useResolveColor();
 * const bgColor = resolveColor(state.backgroundColor);
 * ```
 *
 * @returns A function that resolves ColorValue to hex string
 */
export const useResolveColor = (): ResolveColorFn => {
  const { themeColors } = useThemeColors();

  const resolve = useCallback(
    (
      colorValue: ColorValue | string | undefined | null,
      defaultHex: string = "#000000",
    ): string => {
      return resolveColor(colorValue, themeColors, defaultHex);
    },
    [themeColors],
  );

  return resolve;
};
